import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { toast } from "sonner";
import { Hash, Volume2, Plus, Trash2 } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

export default function ChannelsPanel() {
  const { active } = useServer();
  const [channels, setChannels] = useState([]);
  const [form, setForm] = useState({ name: "", type: "text" });

  const guildId = active?.id;

  const refresh = () => {
    if (!guildId || !active.bot_present) return;
    api.get(`/bot/channels?guild_id=${guildId}`).then((r) => setChannels(r.data));
  };

  useEffect(() => { refresh(); }, [guildId, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      await api.post(`/bot/channels?guild_id=${guildId}`, form);
      toast.success(`Channel '${form.name}' created`);
      setForm({ name: "", type: "text" });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete channel "${name}"?`)) return;
    try {
      await api.delete(`/bot/channels/${id}?guild_id=${guildId}`);
      toast("Channel deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const grouped = {
    text: channels.filter((c) => c.type === "text"),
    voice: channels.filter((c) => c.type === "voice"),
    category: channels.filter((c) => c.type === "category"),
  };

  return (
    <div data-testid="channels-panel" className="space-y-8">
      <div>
        <div className="overline mb-2">// COMMS NETWORK</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Channels</h1>
        <p className="text-zinc-400 mt-2">Managing channels in <span className="text-white">{active.name}</span></p>
      </div>

      <form onSubmit={create} className="tactical-card p-6 grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="channel-create-form">
        <div className="md:col-span-6">
          <label className="overline block mb-2">Channel name</label>
          <input className="tactical-input" placeholder="lfg-warzone" value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })}
                 data-testid="channel-name-input" />
        </div>
        <div className="md:col-span-3">
          <label className="overline block mb-2">Type</label>
          <select className="tactical-input" value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  data-testid="channel-type-select">
            <option value="text">Text</option>
            <option value="voice">Voice</option>
          </select>
        </div>
        <div className="md:col-span-3 flex items-end">
          <button type="submit" className="btn-primary w-full justify-center" data-testid="channel-create-btn">
            <Plus className="w-4 h-4" /> Create
          </button>
        </div>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="tactical-card p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-xl font-bold uppercase">Text Channels</h2>
            <span className="font-mono-tabular text-xs text-zinc-500">{grouped.text.length}</span>
          </div>
          <div className="space-y-2">
            {grouped.text.map((c) => (
              <ChannelRow key={c.id} channel={c} onDelete={remove} icon={Hash} />
            ))}
          </div>
        </div>
        <div className="tactical-card p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-xl font-bold uppercase">Voice Channels</h2>
            <span className="font-mono-tabular text-xs text-zinc-500">{grouped.voice.length}</span>
          </div>
          <div className="space-y-2">
            {grouped.voice.map((c) => (
              <ChannelRow key={c.id} channel={c} onDelete={remove} icon={Volume2} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChannelRow({ channel, onDelete, icon: Icon }) {
  return (
    <div className="border border-brand-line p-3 flex items-center gap-3 hover:border-brand-orange transition-colors rounded-lg" data-testid={`channel-row-${channel.id}`}>
      <Icon className="w-4 h-4 text-brand-orange" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">{channel.name}</div>
        <div className="font-mono-tabular text-[10px] text-zinc-500 uppercase">{channel.members} members</div>
      </div>
      <button onClick={() => onDelete(channel.id, channel.name)} className="text-zinc-500 hover:text-red-400 p-1" data-testid={`channel-delete-${channel.id}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}
